import React, { useState } from 'react';
import { Button, Link, Card, Checkbox, CancelButton } from '../ui';
import GroupIcon from '../../icons/Group';
import { OpenSnackBarDTO } from '../../store/error/types';
import styled, { ThemeProvider } from 'styled-components';
import useTheme from '@material-ui/core/styles/useTheme';
import CloseIcon from '../../icons/Close';
import { naturalSortKeywords } from '../../helpers/keywords';
import { useTranslation, Trans } from 'react-i18next';
import ChevronDownIcon from '../../icons/ChevronDown';
import { useIsMobile } from '../../helpers/window';

interface Props {
  selectedIds: number[];
  keywordById: { [key: number]: Keyword };
  keywordTree: { [key: number]: number[] };
  maxAllowed?: number;
  onSelected: (selectedIds: number[]) => void;
  openSnackBarAction?: (data: OpenSnackBarDTO) => void;
  onBackClick?: () => void;
  saveButtonTitle?: string;
  keywordsLevels: KeywordsLevels;
}

const KeywordPicker: React.FC<Props> = ({
  selectedIds: initSelectedIds,
  keywordById,
  keywordTree,
  maxAllowed,
  onSelected,
  openSnackBarAction,
  onBackClick,
  saveButtonTitle,
  keywordsLevels,
}: Props) => {
  const { t } = useTranslation();
  const [parentSelectedId, setParentSelectedId] = useState<number | undefined>(() => {
    const keys = Object.keys(keywordTree);
    if (!keys.length) return undefined;
    const key = keys[0] as unknown as number;
    return keywordById[key].id;
  });
  const [selectedIds, setSelectedIds] = useState<number[]>(initSelectedIds);
  const [openedParentIds, setOpenedParentIds] = useState<number[]>([]);

  const theme = useTheme();

  const isMobile = useIsMobile();

  const mobileToggleParentOpen = (id: number) => {
    setOpenedParentIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((i) => i !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const renderKeywordCheckboxes = (ids: number[]): React.ReactNode => {
    return naturalSortKeywords(ids.map((id) => keywordById[id])).map((child, index) => {
      const id = child.id;
      const checked = selectedIds.includes(id);

      return (
        <Checkbox
          key={index}
          className="my-2"
          label={child.name}
          checked={checked}
          onChange={() => {
            let newArray: number[];
            if (checked) {
              // Remove item
              newArray = selectedIds.filter((item) => item !== id);
            } else if (maxAllowed && selectedIds.length >= maxAllowed) {
              openSnackBarAction?.({
                message: t(`You can not pick more than {{maxAllowed}} keywords`, {
                  maxAllowed: maxAllowed,
                }),
                severity: 'warning',
              });
              return;
            } else {
              // maxAllowed not exceeded, throw feedback and return.
              newArray = [...selectedIds, id];
            }
            setSelectedIds(newArray);
          }}
        />
      );
    });
  };

  const renderMobileAreaName = (id: number, label: string, selected: boolean): JSX.Element => {
    return (
      <div
        className={`mobileCtrlAreaName ${selected ? 'mobileCtrlAreaNameSelected' : ''}`}
        onClick={() => mobileToggleParentOpen(id)}
      >
        <span>{label}</span>

        <div className={`transform transition-all duration-300 ${selected ? 'rotate-180' : 'rotate-0'}`}>
          <ChevronDownIcon />
        </div>
      </div>
    );
  };

  const renderKeywordGroup = (id: number, label: string, selected: boolean, childrenIds: number[]): JSX.Element => {
    return (
      <div key={id} className="text-sm mobileCtrlKeywordsWrapper">
        {renderMobileAreaName(id, label, selected)}

        <div className={`mobileCtrlKeywordsList ${selected ? 'mobileCtrlAreaSelected' : ''}`}>
          {renderKeywordCheckboxes(childrenIds)}
        </div>
      </div>
    );
  };

  let parentName;
  let children: number[];
  if (keywordsLevels == 1) {
    parentName = '';
    children = Object.values(keywordTree).flat();
  } else if (parentSelectedId) {
    parentName = keywordById[parentSelectedId].name;
    children = keywordTree[parentSelectedId];
  } else {
    parentName = '';
    children = [];
  }

  const keywordsSelected = (
    <>
      <div className="mt-4 mb-1 font-bold mobileCtrlKeywordsSelectedTitle">{t('Keywords selected:')}</div>
      {selectedIds.length > 0 ? (
        <ul className="mobileCtrlUlList">
          {selectedIds.map((id, index) => {
            const child = keywordById[id];
            const parent = child.parent_id ? keywordById[child.parent_id] : undefined;
            return (
              <StyledKeywords
                key={index}
                draggable
                onDragStart={
                  isMobile
                    ? undefined
                    : (e) => {
                        e.dataTransfer.setData('text/plain', JSON.stringify({ id: child.id }));
                      }
                }
                onDragOver={
                  isMobile
                    ? undefined
                    : (e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'copy';
                      }
                }
                onDrop={
                  isMobile
                    ? undefined
                    : (e) => {
                        e.preventDefault();
                        const data = JSON.parse(e.dataTransfer.getData('text/plain'));
                        const { id: origId } = data;
                        const destId = child.id;

                        const origIndex = selectedIds.indexOf(origId);
                        const destIndex = selectedIds.indexOf(destId);

                        const newArray = [...selectedIds];
                        if (origIndex !== -1 && destIndex !== -1) {
                          newArray[origIndex] = destId;
                          newArray[destIndex] = origId;
                        }
                        setSelectedIds(newArray);
                      }
                }
              >
                <div className="flex w-full cursor-pointer justify-between bg-transparent mb-1 mobileCtrlKeywordCard">
                  <div className="flex w-full py-2 px-4 cursor-pointer">
                    {isMobile ? (
                      <button
                        type="button"
                        className={`bg-transparent p-2 ${index <= 0 ? 'invisible' : ''}`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedIds((prev) => {
                            if (index <= 0) return prev; //If is the first element returns the same array

                            const copy = [...prev]; // Copy of the array
                            [copy[index - 1], copy[index]] = [copy[index], copy[index - 1]]; //Swap in place
                            return copy;
                          });
                        }}
                        aria-label="Move up"
                      >
                        ▲
                      </button>
                    ) : (
                      <GroupIcon />
                    )}
                    <div className="ml-3">
                      {keywordsLevels > 1 && parent && (
                        <>
                          <Link>{parent.name}</Link>
                          <span className="ml-1 mr-1 mobileCtrlKeywordArrow">{'>'}</span>
                        </>
                      )}
                      <span>{child.name}</span>
                    </div>
                  </div>

                  <button
                    onClick={(e) => {
                      const newArray = selectedIds.filter((item) => item !== id);
                      setSelectedIds(newArray);
                    }}
                    className="bg-transparent p-2.5 border-l-2 border-white border-solid"
                  >
                    <CloseIcon />
                  </button>
                </div>
              </StyledKeywords>
            );
          })}
        </ul>
      ) : (
        <p>{t('No keywords selected')}</p>
      )}
    </>
  );

  const SINGLE_LEVEL_PARENT_ID = -1;
  const isSingleSelected = openedParentIds.includes(SINGLE_LEVEL_PARENT_ID);

  return (
    <ThemeProvider theme={theme}>
      <div className="text-sm max-w-4xl mobileCtrlKeywordPickerContent" style={{ flexGrow: 1 }}>
        {Object.keys(keywordTree).length > 0 ? (
          <>
            <div className="mb-8">
              <p>
                {t(
                  'Please choose keywords from the list below. The order matters — your first keyword is the most important and represents the main area.',
                )}
              </p>
              <p>
                {isMobile
                  ? t('You can reorder your selection.')
                  : t('You can reorder your selection by dragging and dropping the keywords.')}
              </p>

              {!isMobile && keywordsSelected}
            </div>

            {!isMobile ? (
              <StyledColumnWrapper
                keywordsLevels={keywordsLevels}
                className="grid grid-flow-col auto-cols-max mt-3 gap-5 mb-3"
              >
                {keywordsLevels > 1 && (
                  <Card>
                    <p>
                      <strong>{t('Area')}</strong>
                    </p>
                    <div className="keywordCategoryLine border-b border-gray-200 mt-3 mb-3" />
                    <ol>
                      {Object.keys(keywordTree).map((id, index) => {
                        const parent = keywordById[id as unknown as number];
                        const selected: boolean = parentSelectedId === parent.id;
                        const item = (
                          <Link
                            className="cursor-pointer"
                            onClick={(e) => {
                              setParentSelectedId(parent.id);
                            }}
                          >
                            {parent.name}
                          </Link>
                        );
                        return (
                          <li className="mt-1 mb-3" key={index}>
                            {selected ? <strong>{item}</strong> : item}
                          </li>
                        );
                      })}
                    </ol>
                  </Card>
                )}
                {(keywordsLevels == 1 || parentSelectedId) && (
                  <Card>
                    <p>
                      {keywordsLevels == 1 ? (
                        <strong>Keywords</strong>
                      ) : (
                        <Trans i18nKey="keywordsRelatedWithParent">
                          <strong>Keywords</strong> related with <strong>{{ parentName }}</strong>
                        </Trans>
                      )}
                    </p>
                    <div className="keywordCategoryLine border-b border-gray-200 mt-3 mb-3" />
                    <div className="column flex flex-col items-start">{renderKeywordCheckboxes(children)}</div>
                  </Card>
                )}
              </StyledColumnWrapper>
            ) : (
              <>
                <div className="mobileCtrlKeywords">
                  {/* With areas defined */}
                  {keywordsLevels > 1 &&
                    Object.keys(keywordTree).map((id, index) => {
                      const parent = keywordById[id as unknown as number];
                      const isParentSelected: boolean = openedParentIds.includes(parent.id);
                      const mobileChildren = keywordTree[parent.id];

                      return renderKeywordGroup(parent.id, parent.name, isParentSelected, mobileChildren);
                    })}
                  {/* Without areas defined */}
                  {keywordsLevels == 1 &&
                    renderKeywordGroup(SINGLE_LEVEL_PARENT_ID, t('Keywords'), isSingleSelected, children)}
                </div>
                {keywordsSelected}
              </>
            )}
          </>
        ) : (
          <p className="text-md" style={{ width: '44rem' }}>
            {t('There are no keywords.')}
          </p>
        )}
      </div>

      {/* FOOTER BUTTONS */}
      {Object.keys(keywordTree).length > 0 && (
        <StyledButtonsWrapper className="keywordsFooterButtons mobileCtrlKeywordsFooterButtonsWrapper">
          {onBackClick && (
            <CancelButton className="mobileCtrlKeywordsFooterButton" onClick={onBackClick}>
              {t('Cancel')}
            </CancelButton>
          )}
          {Object.keys(keywordTree).length > 0 && (
            <Button
              className="w-48 mx-2 mobileCtrlKeywordsFooterButton"
              onClick={() => {
                onSelected(selectedIds);
                onBackClick && onBackClick();
              }}
            >
              {saveButtonTitle ? saveButtonTitle : t('Save')}
            </Button>
          )}
        </StyledButtonsWrapper>
      )}
    </ThemeProvider>
  );
};

export default KeywordPicker;

const StyledKeywords = styled.li`
  > div {
    background-color: rgba(229, 231, 235, 0.7);
  }
`;

const StyledColumnWrapper = styled.div<{ keywordsLevels: KeywordsLevels }>`
  grid-template-columns: ${(props) => `repeat(${props.keywordsLevels}, minmax(0, 1fr))`};

  > div {
    height: 22rem;
    overflow-y: scroll;
    padding: 0;

    > p {
      padding: 1.25rem 1.25rem 0 1.25rem;
      position: sticky;
      top: 0;
      background-color: #fff;
    }

    > div {
      position: sticky;
      top: 40px;
      margin: 0;
      padding-top: 0;
      padding-bottom: 0.75rem;
      background-color: #fff;
    }

    > ol,
    .column {
      padding-left: 1.25rem;
      padding-right: 1.25rem;
      padding-top: 1rem;
    }
  }
`;

const StyledButtonsWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: sticky;
  width: 100%;
  bottom: 0;
  padding: 1rem 0;
  background-color: ${(props) => props.theme.palette.background.paper};
  border-top: 1px dashed ${(props) => props.theme.palette.secondary.dark};
  z-index: 2;
`;
